/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { CompetencyLevelV3, SaveCompetencyRequest } from '../competence/competence.model';
import { LanguageValue } from '../diagnostics/diagnostics.model';
import { Framework, FrameworkCompetencyV3, FrameworkTag, FrameworkTagCompetence, FrameworkType } from './admin.model';

export const ESCO_FRAMEWORK_ID = 'esco-id';
const ESCO_ITEM_COUNT = 13939;

export interface FrameworkLevelV3 {
  uid?: string;
  name: string;
  value: string;
  color?: string | null;
}

export interface FrameworkLevelSchemeV3 {
  enabled: boolean;
  competencyUid: string | null;
  global: boolean;
  customized: boolean;
  levels: FrameworkLevelV3[];
}

export interface FrameworkTagV3 extends Omit<FrameworkTag, 'competency' | 'children'> {
  children?: FrameworkTagV3[];
  competency?: FrameworkTagCompetence;
}

export interface FrameworkV3 extends Omit<Framework, 'tags'> {
  color?: string | null;
  itemCount?: number;
  ownedByOrganization?: boolean;
  levelScheme?: FrameworkLevelSchemeV3;
  tags: FrameworkTagV3[];
}

export interface FrameworkLibraryTagSummaryV3 {
  title: string;
  childCount: number;
}

export interface FrameworkLibraryEntryV3 {
  id: string;
  name: string;
  author: string;
  organizationUid: string;
  description: string;
  type: FrameworkType;
  itemCount: number;
  color?: string;
  active: boolean;
  topLevelTags: FrameworkLibraryTagSummaryV3[];
}

export interface FrameworkTreeNodeV3Request {
  tagId?: string;
  title: string;
  description: string;
  /** Default language first, then one entry per translated language. */
  titles?: LanguageValue[];
  descriptions?: LanguageValue[];
  children: FrameworkTreeNodeV3Request[];
}

export interface FrameworkTreeV3Request {
  title?: string;
  description?: string;
  color?: string;
  competencyUid?: string | null;
  tags: FrameworkTreeNodeV3Request[];
}

/** Error of a framework tree save rejected because one of its titles is already taken by another item of the organization. */
export const FRAMEWORK_TREE_TITLE_TAKEN_ERROR = 'FRAMEWORK_TREE_TITLE_TAKEN';

export const FRAMEWORK_COLOR_PRESETS: string[] = [
  '#ef4444',
  '#f97316',
  '#f59e0b',
  '#eab308',
  '#22c55e',
  '#10b981',
  '#14b8a6',
  '#06b6d4',
  '#0ea5e9',
  '#3b82f6',
  '#6356e5',
  '#8b5cf6',
  '#ec4899',
  '#e11d48',
];

export const MIN_CUSTOM_LEVELS = 2;

export const MAX_CUSTOM_LEVELS = 10;

export function defaultCustomLevels(): FrameworkLevelV3[] {
  return [
    { name: '', value: '1' },
    { name: '', value: '2' },
    { name: '', value: '3' },
  ];
}

export function countFrameworkItems(tags: FrameworkTagV3[] | undefined): number {
  if (!tags?.length) {
    return 0;
  }
  return tags.reduce((sum, tag) => sum + 1 + countFrameworkItems(tag.children), 0);
}

export function frameworkItemCount(frameworkUid: string | undefined, tags: FrameworkTagV3[] | undefined): number {
  return frameworkUid === ESCO_FRAMEWORK_ID ? ESCO_ITEM_COUNT : countFrameworkItems(tags);
}

function isHexColor(value: string | null | undefined): value is string {
  return !!value && /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(value);
}

function localizedValue(entries: { languageCode: string; value: string }[] | undefined, langCode: string): string {
  if (!entries?.length) {
    return '';
  }
  return (entries.find((entry) => entry.languageCode === langCode) ?? entries[0]).value;
}

export function competencyTitle(competency: { title: { languageCode: string; value: string }[] }, langCode: string): string {
  return localizedValue(competency.title, langCode);
}

export function competencyToLevels(levels: CompetencyLevelV3[] | undefined, langCode: string): FrameworkLevelV3[] {
  return (levels ?? []).map((level, index) => ({
    uid: level.uid ?? undefined,
    name: localizedValue(level.title, langCode),
    value: localizedValue(level.shortTitle, langCode) || `${level.rank ?? index + 1}`,
    color: level.color ?? null,
  }));
}

export function competencyToScheme(competency: FrameworkCompetencyV3 | null | undefined, langCode: string): FrameworkLevelSchemeV3 {
  if (!competency) {
    return { enabled: false, competencyUid: null, global: false, customized: false, levels: [] };
  }
  return {
    enabled: true,
    competencyUid: competency.competencyUid,
    global: competency.global,
    customized: false,
    levels: competencyToLevels(competency.levels, langCode),
  };
}

export function schemeToSaveCompetencyRequest(scheme: FrameworkLevelSchemeV3, langCode: string): SaveCompetencyRequest {
  return {
    title: null,
    global: false,
    levels: scheme.levels.map((level, index) => {
      const shortValue = level.value?.trim();
      return {
        uid: level.uid ?? null,
        title: [{ languageCode: langCode, value: level.name.trim() }],
        description: [],
        rank: index + 1,
        color: isHexColor(level.color) ? level.color : null,
        shortTitle: shortValue ? [{ languageCode: langCode, value: shortValue }] : [],
      };
    }),
  };
}
